import type { parseCreditAccountTransaction } from "@gearbox-protocol/sdk/history";
import type { OnchainSDK } from "@gearbox-protocol/sdk/onchain";
import type { Address, Hex, Log } from "viem";
import {
  getAddress,
  isAddressEqual,
  type TransactionReceipt,
  toEventSelector,
} from "viem";

export type ParseTransactionInput = Parameters<
  typeof parseCreditAccountTransaction
>[0];

const FACADE_EVENT_SIGS = [
  "OpenCreditAccount(address,address,address,uint256)",
  "CloseCreditAccount(address,address)",
  "LiquidateCreditAccount(address,address,address,uint256)",
  "PartiallyLiquidateCreditAccount(address,address,address,uint256,uint256,uint256)",
  "StartMultiCall(address,address)",
] as const;

const FACADE_TOPICS: Set<Hex> = new Set(
  FACADE_EVENT_SIGS.map(sig => toEventSelector(`event ${sig}`)),
);

export interface CreditContext {
  creditFacade: Address;
  creditAccount: Address;
}

/**
 * Scans the receipt logs for the credit-facade events that pin down which
 * `(creditFacade, creditAccount)` pair the transaction operates on.
 *
 * Returns the first match. If the transaction touches multiple credit
 * accounts only the first one is parsed (callers may log the rest).
 */
export function findCreditContextFromLogs(
  logs: readonly Log<bigint | number, number, false>[],
): CreditContext | null {
  for (const log of logs) {
    const topic0 = log.topics?.[0];
    const topic1 = log.topics?.[1];
    if (!topic0 || !topic1) continue;
    if (!FACADE_TOPICS.has(topic0)) continue;
    return {
      creditFacade: getAddress(log.address),
      creditAccount: getAddress(`0x${topic1.slice(-40)}` as Hex),
    };
  }
  return null;
}

export class NotACreditAccountTxError extends Error {
  constructor() {
    super(
      "Transaction does not contain any Gearbox credit-facade events " +
        "(OpenCreditAccount, CloseCreditAccount, LiquidateCreditAccount, " +
        "PartiallyLiquidateCreditAccount, StartMultiCall).",
    );
    this.name = "NotACreditAccountTxError";
  }
}

export class CreditFacadeNotInSdkError extends Error {
  constructor(creditFacade: Address) {
    super(
      `Credit facade ${creditFacade} not found in the SDK market register. ` +
        "Check that the SDK is attached to the right network and block.",
    );
    this.name = "CreditFacadeNotInSdkError";
  }
}

/**
 * Combines log-scan with SDK lookups to produce a {@link ParseTransactionInput}
 * (minus `trace`).
 */
export function buildParseInput(
  sdk: OnchainSDK,
  receipt: TransactionReceipt<bigint | number>,
  trace: unknown,
): ParseTransactionInput {
  const ctx = findCreditContextFromLogs(receipt.logs);
  if (!ctx) {
    throw new NotACreditAccountTxError();
  }
  const cm = sdk.marketRegister.creditManagers.find(c =>
    isAddressEqual(c.creditFacade.address, ctx.creditFacade),
  );
  if (!cm) {
    throw new CreditFacadeNotInSdkError(ctx.creditFacade);
  }
  const market = sdk.marketRegister.findByCreditManager(
    cm.creditManager.address,
  );
  return {
    trace,
    receipt,
    creditFacade: ctx.creditFacade,
    creditAccount: ctx.creditAccount,
    creditManager: cm.creditManager.address,
    underlying: cm.underlying,
    pool: market.pool.pool.address,
    register: sdk,
  };
}
